
import Vue, { PropType } from 'vue';
import { VFileUploaderFile, VFileUploader } from '@/components/w2pFileUploader';
import { supportedExtensions } from '@/constants/supportedExtensions';
import { ProductPrintPositionSetterType } from '@/setters/typings/common/ProductPrintPositionSetterType';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useLangStore } from '@/stores/lang';
import { mapState } from 'pinia';
import { LocaleMessage } from 'vue-i18n';
import {
  CvAccordion,
  CvAccordionItem,
  CvFileUploader,
  CvCheckbox,
} from '@carbon/vue';
import Document32 from '@carbon/icons-vue/es/document/32';
import isValidProofApprovalContext from '@/validators/proofApprovalContextValidator';
import { UploadLogo } from '../../../upload-logo';
import { SelectedPrintPositionData } from '@/web-components/upload-logo/typings';
import { SupportedLangCodes } from '@/constants/languageCodes';
import isValidLanguageValidator from '@/validators/langLocaleValidator';
import { ArtworkPosition } from '@/components/w2pFileUploader/typings/Artwork';
import { ProductPrintVariant } from '@midocean/webapps-shared-domain';
import { validateFn } from '@/validators/basicFileValidator';

export default Vue.extend({
  components: {
    CvAccordion,
    CvAccordionItem,
    CvFileUploader,
    CvCheckbox,
    Document32,
    UploadLogo,
  },
  inheritAttrs: false,
  props: {
    languageCode: {
      type: String as PropType<SupportedLangCodes>,
      required: false,
      default: 'en',
      validator: isValidLanguageValidator,
    },
    propPrintPositions: {
      type: [] as PropType<ProductPrintPositionSetterType[]>,
      default: [],
    },
    selectedPrintPosition: {
      type: Object,
      default: () => {
        return {};
      },
    },
    sapCustomerId: {
      type: String,
      required: true,
    },
    isSameLogoForAllPrintPositions: {
      type: Boolean,
      default: false,
    },
    masterProductSku: {
      type: String,
      required: true,
    },
    context: {
      type: String as PropType<'Customer' | 'CustomerCare' | 'PrePress'>,
      required: true,
      validator: isValidProofApprovalContext,
    },
    selectedMethod: {
      type: String,
      required: true,
    },
    numberOfColorsPerPrintPosition: {
      type: Array as PropType<{ positionId: string; numberOfColors: number }[]>,
      default() {
        return [];
      },
    },
    customerCompanyLogoUrl: {
      type: String,
      required: false,
      default: '',
    },
    tok: {
      type: String,
      required: true,
    },
    selectedPrintPositionData: {
      type: Array as PropType<SelectedPrintPositionData[]>,
      required: false,
      default() {
        return [];
      },
    },
    productVariantColor: {
      type: String,
      required: false,
      default: '',
    },
    artworkPositionMap: {
      type: Array as PropType<ArtworkPosition[]>,
      default() {
        return [];
      },
    },
  },

  data() {
    return {
      /**
       * Reference to CvFileUploader component
       */
      fileUploaderRef: null as unknown,
      files: [] as unknown as VFileUploaderFile[],
      supportedExtensions,
      maxFileSize: 20,
      end: 'end',
      isSameLogo: false,
      validateFn,
    };
  },

  mounted() {
    (this.fileUploaderRef as VFileUploader) = this.$refs
      ?.fileUploader as VFileUploader;
  },

  computed: {
    ...mapState(useProductStore, ['data', 'printPositions']),
    ...mapState(useLangStore, ['languageInUse']),

    isEmptyInternalFiles(): boolean {
      return (
        (this.fileUploaderRef as VFileUploader)?.internalFiles.length === 0
      );
    },

    maximumColoursRepresentation(): string | LocaleMessage {
      const position = this.data.productPrintPositions.find(
        (p) => p.name === this.selectedPrintPosition.printPositionSAPCode
      );
      const technique = position?.printTechniques.find((technique) => {
        return (
          technique.printTechnique.code ===
          this.selectedPrintPosition.printTechniqueSAPCode
        );
      });

      return technique?.printTechnique?.isFullColor
        ? `- ${this.$t('print-position.full-colour')}`
        : `- ` +
            this.$t(`print-position.max-colours`) +
            `: ${technique?.maximumColours}`;
    },
    visiblePositions(): ProductPrintPositionSetterType[] {
      if (!this.isSameLogoForAllPrintPositions) {
        return this.propPrintPositions;
      } else {
        return this.propPrintPositions.filter(
          (position) =>
            this.artworkPositionMap.findIndex(
              (art) => art.position === position.printPositionSAPCode
            ) !== -1
        );
      }
    },
  },

  methods: {
    setState(idx: number, state = '') {
      !this.isEmptyInternalFiles &&
        (this.fileUploaderRef as VFileUploader).setState(idx, state);
    },

    //  * Wrapper around CvFileUploader internal method
    //  */
    setInvalidMessage(idx: number, msg: string) {
      !this.isEmptyInternalFiles &&
        (this.fileUploaderRef as VFileUploader).setInvalidMessage(idx, msg);
    },

    isCheckboxDisabled(position: string): boolean {
      return (
        this.artworkPositionMap.findIndex(
          (art) => art.position === position
        ) === -1
      );
    },
    selectedVariantImageUrl(position: any): string | undefined {
      const pp = this.data.productPrintPositions.find(
        (p) => p.name === position.printPositionSAPCode
      );
      let selectedVariant = this.data.variants.find(
        (varImg) => varImg.colour === this.productVariantColor
      ) as ProductPrintVariant;
      const variantImage = selectedVariant.images.find(
        (img) => img.printPositionID === pp?.id
      );
      return variantImage?.imageThumbnailUrl || '';
    },
    handleSameLogo(position: string) {
      this.$emit('same-logo', !this.isSameLogoForAllPrintPositions, position);
      this.$emit('update:next-button');
    },
    handleAdditionalFiles(files: VFileUploaderFile[]) {
      if (!files[0]) {
        return;
      }

      files.forEach((file, index) => {
        const { size, type } = file.file;
        const validationObj = this.validateFn({
          type: type || 'image/psd',
          size,
          maxFileSize: this.maxFileSize,
          supportedExtensions: this.supportedExtensions,
        });
        const errorAny = Object.values(validationObj).some(
          ({ valid }) => !valid
        );

        if (errorAny) {
          let error = Object.values(validationObj).find(
            ({ valid }: { valid: boolean }) => !valid
          );

          if (error?.errorMsg === 'invalid-file-size') {
            this.setInvalidMessage(
              index,
              this.$tc(
                `upload-logo.inline-notification.${error.errorMsg}`,
                error.value,
                { value: error.value }
              ).toString()
            );
          }
          if (error?.errorMsg === 'invalid-file-type') {
            this.setInvalidMessage(
              index,
              this.$t(
                `upload-logo.inline-notification.${error?.errorMsg}`
              ).toString()
            );
          }

          this.setState(index, '');
          return;
        } else {
          if (files[0]) {
            this.$emit('update:additional-files', files[0]);
          } else {
            this.$emit('update:additional-files', files);
          }
        }
      });
    },
    techniqueNameTranslated(
      printPositionSAPCode: string,
      printTechniqueSAPCode: string
    ): string {
      const position = this.data.productPrintPositions.find(
        (p) => p.name === printPositionSAPCode
      );
      const technique = position?.printTechniques.find((technique) => {
        return technique.printTechnique.code === printTechniqueSAPCode;
      });
      const techniqueName = technique?.printTechnique.nameTranslations.find(
        ({ language }) => language.code === this.languageInUse
      )?.text;
      return techniqueName || '';
    },
  },
});
